import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { 
  LayoutDashboard, Plus, LogOut, Shield, Menu, X, ChevronRight, User
} from "lucide-react";
import { useState } from "react";

/**
 * AppLayout - Layout wrapper for app pages (after login)
 * Used on: /dashboard, /full-audit, /audit/:id, /report/:id
 * 
 * Sidebar: Dashboard, New Audit, (Admin if admin), Account info, Logout
 * Breadcrumbs passed as prop
 */
export function AppLayout({ children, breadcrumbs = [] }) {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/full-audit', label: 'New Audit', icon: Plus },
  ];

  if (isAdmin) {
    navItems.push({ path: '/admin', label: 'Admin Panel', icon: Shield, adminOnly: true });
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-56 bg-[#0A0A0A] border-r border-white/5 fixed left-0 top-0 bottom-0 z-40">
        {/* Logo */}
        <div className="p-4 border-b border-white/5">
          <Link to="/" className="flex items-center">
            <img src="/scorecite-logo.png" alt="ScoreCite" className="h-8" />
          </Link>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map(({ path, label, icon: Icon, adminOnly }) => (
            <Link key={path} to={path}>
              <Button 
                variant="ghost" 
                className={`w-full justify-start ${
                  isActive(path) 
                    ? adminOnly 
                      ? 'text-purple-400 bg-purple-500/10' 
                      : 'text-blue-400 bg-blue-500/10'
                    : adminOnly
                      ? 'text-purple-400/70 hover:text-purple-400 hover:bg-purple-500/5'
                      : 'text-zinc-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Icon className="w-4 h-4 mr-3" /> {label}
              </Button>
            </Link>
          ))}
        </nav>

        {/* User Info + Logout */}
        <div className="p-3 border-t border-white/5">
          <div className="px-3 py-2 mb-2">
            <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Account</p>
            <p className="text-zinc-300 text-sm truncate flex items-center gap-2">
              <User className="w-3 h-3 text-zinc-500" />
              {user?.email}
            </p>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-zinc-500 hover:text-white hover:bg-white/5"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-3" /> Logout
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 h-14 bg-[#0A0A0A] border-b border-white/10 flex items-center justify-between px-4">
        <Link to="/">
          <img src="/scorecite-logo.png" alt="ScoreCite" className="h-7" />
        </Link>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 bg-[#121212] border border-white/10 rounded-lg"
        >
          {sidebarOpen ? <X className="w-5 h-5 text-white" /> : <Menu className="w-5 h-5 text-white" />}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
          {/* Sidebar */}
          <aside 
            className="relative w-72 bg-[#0A0A0A] h-full border-r border-white/10 pt-16 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <nav className="flex-1 p-4 space-y-2">
              {navItems.map(({ path, label, icon: Icon, adminOnly }) => (
                <Link key={path} to={path} onClick={() => setSidebarOpen(false)}>
                  <Button 
                    variant="ghost" 
                    className={`w-full justify-start text-base py-6 ${
                      isActive(path) 
                        ? adminOnly ? 'text-purple-400 bg-purple-500/10' : 'text-blue-400 bg-blue-500/10'
                        : adminOnly ? 'text-purple-400/70 hover:text-purple-400' : 'text-zinc-300 hover:text-white'
                    }`}
                  >
                    <Icon className="w-5 h-5 mr-3" /> {label}
                  </Button>
                </Link>
              ))}
            </nav>
            <div className="p-4 border-t border-white/10">
              <p className="text-zinc-500 text-sm px-3 mb-3 truncate">{user?.email}</p>
              <Button 
                variant="ghost" 
                className="w-full justify-start text-zinc-400 hover:text-white py-5"
                onClick={() => { handleLogout(); setSidebarOpen(false); }}
              >
                <LogOut className="w-5 h-5 mr-3" /> Logout
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 md:ml-56 pt-14 md:pt-0 w-full max-w-[100vw] overflow-x-hidden">
        {/* Breadcrumbs */}
        {breadcrumbs.length > 0 && (
          <div className="px-4 md:px-6 py-3 border-b border-white/5 flex items-center gap-2 text-sm overflow-x-auto scrollbar-hide whitespace-nowrap">
            {breadcrumbs.map((crumb, index) => (
              <span key={index} className="flex items-center gap-2 flex-shrink-0">
                {index > 0 && <ChevronRight className="w-3 h-3 text-zinc-600" />}
                {crumb.href ? (
                  <Link to={crumb.href} className="text-zinc-400 hover:text-white transition-colors">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-zinc-300">{crumb.label}</span>
                )}
              </span>
            ))}
          </div>
        )}
        
        {children}
      </main>
    </div>
  );
}

export default AppLayout;
